import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Search, ShieldCheck, X } from 'lucide-react';
import { api, ApiError } from '../../api/cliente.js';
import { Botao } from '../../componentes/ui/Botao.js';
import { Modal } from '../../componentes/ui/Modal.js';
import { Pill } from '../../componentes/ui/Pill.js';
import { SecaoRollover } from '../../componentes/SecaoRollover.js';
import { ListaDs } from '../../componentes/ListaDs.js';
import { ModalImportar } from './ModalImportar.js';
import { ModalMassa } from './ModalMassa.js';

interface Registro {
  ip: string;
  ptr: string | null;
  ttl: number | null;
  fcrdns: 'confirmado' | 'divergente' | 'nao_verificado';
  fcrdnsErro?: string | null;
}

interface SubZona {
  cidr: string;
  arpa: string;
  offsetInicio: string;
}

interface Pagina {
  bloco: { id: string; cidr: string; versao: 'V4' | 'V6' };
  enumeravel: boolean;
  totalEnderecos: string;
  registros: Registro[];
  proximoOffset: string | null;
  subZonas: SubZona[];
  sugestoesMascara: { rotulo: string; mascara: string }[];
}

export function RegistrosPtr() {
  const { blocoId = '' } = useParams();
  const qc = useQueryClient();

  const [offset, setOffset] = useState('0');
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set());
  const [edicao, setEdicao] = useState<Registro | null>(null);
  const [massaAberta, setMassaAberta] = useState(false);
  const [importarAberto, setImportarAberto] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['ptr', blocoId, offset],
    queryFn: () => api.get<Pagina>(`/blocos/${blocoId}/ptr?offset=${offset}&limite=256`),
  });

  const registros = useMemo(() => data?.registros ?? [], [data]);

  const [searchParams] = useSearchParams();
  const ipFoco = searchParams.get('ip');
  const linhaFoco = useRef<HTMLTableRowElement>(null);
  // Qual `?ip=` já foi usado para saltar. Sem isto, o efeito re-dispararia a
  // cada refetch e forçaria o offset de volta ao /24 do IP — travando o seletor
  // de sub-bloco quando o operador tenta trocar depois de chegar pela busca.
  const ipJumpFeito = useRef<string | null>(null);

  // Vindo da busca com ?ip=, salta UMA vez para o /24 que contém o endereço. As
  // subZonas só existem em IPv4, então casar os três primeiros octetos com o
  // /24 (o ponto final evita 45.180.3 casar com 45.180.30) basta e dispensa
  // trazer o ipam — CJS — para o bundle do front.
  useEffect(() => {
    if (!ipFoco || ipJumpFeito.current === ipFoco || !data?.subZonas?.length) return;
    ipJumpFeito.current = ipFoco; // marca antes de mexer: é um salto só por IP.
    const sz = data.subZonas.find((s) => {
      const base = s.cidr.split('/')[0]?.split('.').slice(0, 3).join('.') ?? '';
      return base !== '' && ipFoco.startsWith(`${base}.`);
    });
    if (sz && sz.offsetInicio !== offset) setOffset(sz.offsetInicio);
  }, [ipFoco, data?.subZonas, offset]);

  // Rola até a linha do endereço focado assim que ela aparece na página.
  useEffect(() => {
    if (ipFoco && linhaFoco.current) linhaFoco.current.scrollIntoView({ block: 'center' });
  }, [ipFoco, registros]);

  const recarregar = () => {
    void qc.invalidateQueries({ queryKey: ['ptr', blocoId] });
    setSelecionados(new Set());
  };

  const validar = useMutation({
    // Cria uma tarefa em segundo plano e volta na hora — não prende a tela. O
    // offset garante que valida o /24 aberto. O progresso e o resultado aparecem
    // no indicador de tarefas do topo, e a coluna FCrDNS atualiza ao concluir.
    mutationFn: () =>
      api.post<{ criada: boolean; mensagem?: string }>(
        `/fcrdns/bloco/${blocoId}?offset=${offset}`,
      ),
    // Faz o indicador de tarefas pegar a nova tarefa na hora.
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['tarefas'] }),
  });

  // Cria os A pareados dos PTRs desta página, sem reescrever o reverso. É a
  // correção do FCrDNS "sem registro A" (reverso importado, por exemplo).
  const sincDiretos = useMutation({
    mutationFn: () =>
      api.post<{ criados: number; pulados: number; total: number }>(
        `/blocos/${blocoId}/ptr/diretos?offset=${offset}`,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['ptr', blocoId] }),
  });

  function alternar(ip: string) {
    setSelecionados((s) => {
      const novo = new Set(s);
      if (novo.has(ip)) novo.delete(ip);
      else novo.add(ip);
      return novo;
    });
  }

  const todosMarcados = registros.length > 0 && registros.every((r) => selecionados.has(r.ip));

  if (isLoading) return <Esqueleto />;
  if (!data) return null;

  // Quando nada está marcado, a ação em massa vale para a página inteira — é o
  // que o operador espera ao clicar em "editar em massa" sem selecionar nada.
  const alvoMassa = selecionados.size > 0 ? [...selecionados] : registros.map((r) => r.ip);

  // Blocos IPv4 maiores que /24 vêm com os /24 e seus offsets: aí o operador
  // escolhe qual editar, em vez de paginar até ele.
  const temSubZonas = data.subZonas.length > 0;
  const subZonaAtiva = data.subZonas.find((s) => s.offsetInicio === offset) ?? data.subZonas[0];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-mono text-[15px] font-bold">{data.bloco.cidr}</p>
          <p className="text-[12.5px] text-texto-suave">
            {Number(data.totalEnderecos).toLocaleString('pt-BR')} endereços ·{' '}
            {registros.filter((r) => r.ptr).length} com reverso nesta página
          </p>
        </div>
        <div className="flex gap-2">
          <Botao
            onClick={() => validar.mutate()}
            carregando={validar.isPending}
            disabled={registros.filter((r) => r.ptr).length === 0}
            title="Consulta um resolver externo para conferir se o reverso está visível e se o direto volta ao mesmo IP"
          >
            Validar FCrDNS
          </Botao>
          <Botao
            onClick={() => sincDiretos.mutate()}
            carregando={sincDiretos.isPending}
            disabled={registros.filter((r) => r.ptr).length === 0}
            title="Cria os registros diretos (A) dos reversos desta página, sem reescrever o PTR — conserta o FCrDNS 'sem registro A'"
          >
            Sincronizar diretos
          </Botao>
          <Botao
            onClick={() => setImportarAberto(true)}
            title="Traz os reversos que a operadora já tem no provedor atual, antes de delegar o bloco"
          >
            Importar
          </Botao>
          <Botao variante="primario" onClick={() => setMassaAberta(true)}>
            Editar em massa
          </Botao>
        </div>
      </div>

      {validar.data &&
        (validar.data.criada ? (
          <p className="rounded-card border border-info/20 bg-info-fundo px-4 py-2.5 text-[13px] text-info">
            Validação iniciada em segundo plano
            {temSubZonas && subZonaAtiva ? ` para o ${subZonaAtiva.cidr}` : ''} — acompanhe o
            progresso no ícone de tarefas, no topo. A coluna FCrDNS atualiza sozinha ao terminar.
          </p>
        ) : (
          <p className="rounded-card border border-borda bg-fundo px-4 py-2.5 text-[13px] text-texto-medio">
            {validar.data.mensagem ?? 'Nenhum endereço com reverso nesta página.'}
          </p>
        ))}

      {sincDiretos.data && (
        <p className="rounded-card border border-sucesso/30 bg-sucesso-fundo px-4 py-2.5 text-[13px] text-sucesso">
          {sincDiretos.data.criados} registro(s) direto(s) criado(s)
          {sincDiretos.data.pulados > 0
            ? ` · ${sincDiretos.data.pulados} fora do domínio (pulado)`
            : ''}{' '}
          — agora rode <strong>Validar FCrDNS</strong> para confirmar.
        </p>
      )}

      <SecaoDnssecBloco blocoId={blocoId} />

      {!data.enumeravel && (
        // Não é um aviso de erro: é a explicação de por que a tela mudou de
        // forma. Sem isso o operador acharia que o bloco está vazio.
        <div className="flex items-start gap-2.5 rounded-card border border-info/20 bg-info-fundo px-4 py-3 text-[13px] text-info">
          <Search size={16} className="mt-0.5 shrink-0" />
          <p>
            Este bloco tem {Number(data.totalEnderecos).toExponential(2)} endereços — grande
            demais para listar um a um. A tabela mostra apenas os que já têm reverso
            configurado.
          </p>
        </div>
      )}

      {temSubZonas && (
        <div className="flex flex-wrap items-center gap-2 rounded-card border border-borda bg-superficie px-4 py-3">
          <span className="text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave">
            Sub-bloco
          </span>
          <select
            value={subZonaAtiva?.offsetInicio ?? '0'}
            onChange={(e) => {
              setOffset(e.target.value);
              setSelecionados(new Set());
            }}
            className="h-9 rounded-controle border border-borda bg-fundo px-3 font-mono text-[13px] focus:border-marca focus:outline-none"
          >
            {data.subZonas.map((s) => (
              <option key={s.cidr} value={s.offsetInicio}>
                {s.cidr}
              </option>
            ))}
          </select>
          <span className="text-[12px] text-texto-suave">
            Cada /24 é uma página — escolha qual quer editar em massa.
          </span>
        </div>
      )}

      {selecionados.size > 0 && (
        <div className="flex items-center gap-3 rounded-card bg-escuro px-4 py-3 text-white">
          <span className="text-[13.5px] font-bold">
            {selecionados.size} selecionado{selecionados.size > 1 ? 's' : ''}
          </span>
          <button
            onClick={() => setSelecionados(new Set())}
            className="text-[12.5px] font-semibold text-white/60 hover:text-white"
          >
            limpar seleção
          </button>
        </div>
      )}

      <div className="overflow-hidden rounded-card border border-borda bg-superficie">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[680px] border-collapse">
            <thead>
              <tr className="border-b border-borda bg-borda-tenue">
                <th className="w-10 px-[18px] py-3">
                  <input
                    type="checkbox"
                    checked={todosMarcados}
                    onChange={() =>
                      setSelecionados(todosMarcados ? new Set() : new Set(registros.map((r) => r.ip)))
                    }
                    aria-label="Selecionar todos"
                    className="size-4 accent-marca"
                  />
                </th>
                {['Endereço', 'Reverso (PTR)', 'FCrDNS', ''].map((h) => (
                  <th
                    key={h}
                    className="px-[18px] py-3 text-left text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {registros.map((r) => (
                <tr
                  key={r.ip}
                  ref={r.ip === ipFoco ? linhaFoco : undefined}
                  className={`border-t border-borda-suave ${
                    r.ip === ipFoco
                      ? 'bg-tint ring-1 ring-inset ring-marca'
                      : selecionados.has(r.ip)
                        ? 'bg-[#f0f6fd]'
                        : 'hover:bg-borda-tenue'
                  }`}
                >
                  <td className="px-[18px] py-[11px]">
                    <input
                      type="checkbox"
                      checked={selecionados.has(r.ip)}
                      onChange={() => alternar(r.ip)}
                      aria-label={`Selecionar ${r.ip}`}
                      className="size-4 accent-marca"
                    />
                  </td>
                  <td className="px-[18px] py-[11px] font-mono text-[13px]">{r.ip}</td>
                  <td className="px-[18px] py-[11px]">
                    {r.ptr ? (
                      <span className="font-mono text-[13px] font-semibold">{r.ptr}</span>
                    ) : (
                      <span className="text-[13px] italic text-texto-mudo">— sem reverso —</span>
                    )}
                  </td>
                  <td className="px-[18px] py-[11px]">
                    <IndicadorFcrdns estado={r.fcrdns} temPtr={!!r.ptr} erro={r.fcrdnsErro} />
                  </td>
                  <td className="px-[18px] py-[11px] text-right">
                    <Botao tamanho="tabela" onClick={() => setEdicao(r)}>
                      Editar
                    </Botao>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Com o seletor de sub-bloco, cada /24 já é uma página inteira — o
          "carregar mais" só confundiria, virando a página para o próximo /24. */}
      {data.proximoOffset && !temSubZonas && (
        <div className="flex justify-center">
          <Botao onClick={() => setOffset(data.proximoOffset!)}>Carregar mais endereços</Botao>
        </div>
      )}

      {edicao && (
        <ModalEdicao
          registro={edicao}
          blocoId={blocoId}
          aoFechar={() => setEdicao(null)}
          aoSalvar={recarregar}
        />
      )}

      <ModalMassa
        aberto={massaAberta}
        aoFechar={() => setMassaAberta(false)}
        blocoId={blocoId}
        versao={data.bloco.versao === 'V6' ? 6 : 4}
        ips={alvoMassa}
        sugestoes={data.sugestoesMascara}
        aoAplicar={recarregar}
      />

      <ModalImportar
        aberto={importarAberto}
        aoFechar={() => setImportarAberto(false)}
        blocoId={blocoId}
        cidr={data.bloco.cidr}
        aoImportar={recarregar}
      />
    </div>
  );
}

/**
 * Estado da checagem FCrDNS.
 *
 * "não verificado" é diferente de "divergente", e a distinção importa: um IP
 * ainda não checado não é um problema, e mostrá-lo em vermelho faria o operador
 * caçar defeito onde não há.
 */
function IndicadorFcrdns({
  estado,
  temPtr,
  erro,
}: {
  estado: Registro['fcrdns'];
  temPtr: boolean;
  erro?: string | null;
}) {
  if (!temPtr) return <span className="text-[13px] text-texto-mudo">—</span>;

  if (estado === 'confirmado') {
    return (
      <Pill tom="ok">
        <Check size={12} className="mr-1" /> Confere
      </Pill>
    );
  }
  if (estado === 'divergente') {
    // O motivo no tooltip: quase sempre é o direto (A) que não resolve — o
    // reverso pode estar certo e o par ainda não fechar. É o que confunde.
    return (
      <span title={erro ?? undefined} className="inline-flex cursor-help">
        <Pill tom="aviso">
          <X size={12} className="mr-1" /> Divergente
        </Pill>
      </span>
    );
  }
  return <Pill tom="neutro">Não verificado</Pill>;
}

function ModalEdicao({
  registro,
  blocoId,
  aoFechar,
  aoSalvar,
}: {
  registro: Registro;
  blocoId: string;
  aoFechar: () => void;
  aoSalvar: () => void;
}) {
  const [valor, setValor] = useState(registro.ptr ?? '');

  const salvar = useMutation({
    mutationFn: () =>
      api.put(`/blocos/${blocoId}/ptr`, { ip: registro.ip, ptr: valor.trim() || null }),
    onSuccess: () => {
      aoSalvar();
      aoFechar();
    },
  });

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo="Editar reverso"
      descricao={registro.ip}
      rodape={
        <>
          <Botao onClick={aoFechar} disabled={salvar.isPending}>
            Cancelar
          </Botao>
          <Botao
            variante="primario"
            onClick={() => salvar.mutate()}
            carregando={salvar.isPending}
          >
            Salvar e publicar
          </Botao>
        </>
      }
    >
      <label className="block">
        <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave">
          Nome reverso (PTR)
        </span>
        <input
          value={valor}
          onChange={(e) => setValor(e.target.value)}
          autoFocus
          placeholder="host-5.rev.exemplo.net.br"
          className="h-11 w-full rounded-controle border border-borda px-3 font-mono text-[13.5px] focus:border-marca focus:outline-none"
        />
        <span className="mt-1.5 block text-[12px] text-texto-suave">
          Deixe em branco para remover o reverso deste endereço.
        </span>
      </label>

      {salvar.error && (
        <p className="mt-4 rounded-chip bg-erro-fundo px-3 py-2 text-[12.5px] font-semibold text-erro">
          {(salvar.error as ApiError).mensagem}
        </p>
      )}
    </Modal>
  );
}

function Esqueleto() {
  return (
    <div className="rounded-card border border-borda bg-superficie p-4">
      {Array.from({ length: 8 }, (_, i) => (
        <div key={i} className="h-11 border-b border-borda-suave last:border-0">
          <div className="h-3 w-1/4 translate-y-4 animate-pulse rounded bg-borda" />
        </div>
      ))}
    </div>
  );
}

interface EstadoDnssecZona {
  zona: string;
  assinada: boolean;
  ds: string[];
  dsPublicado: boolean;
  rollover: {
    status: string;
    keyTagVelho: number | null;
    keyTagNovo: number | null;
    dsNovo: string[];
  } | null;
}

/**
 * DNSSEC das zonas reversas do bloco.
 *
 * Fica aqui, dentro do bloco, e não num menu à parte: DNSSEC é atributo da
 * zona, e as zonas `.arpa` vivem no bloco — igual ao DNSSEC do domínio, que
 * mora dentro do domínio. Um bloco tem várias zonas (um /22 tem quatro), então
 * mostra o DS de cada uma: no registro.br a delegação reversa é feita zona a
 * zona, e cada DS vai ao lado da sua.
 */
function SecaoDnssecBloco({ blocoId }: { blocoId: string }) {
  const qc = useQueryClient();
  const [aberto, setAberto] = useState(false);
  const chave = ['blocos', blocoId, 'dnssec'];

  const { data } = useQuery({
    queryKey: chave,
    queryFn: () => api.get<EstadoDnssecZona[]>(`/blocos/${blocoId}/dnssec`),
  });

  const invalidar = () => qc.invalidateQueries({ queryKey: chave });

  const ativar = useMutation({
    mutationFn: () => api.post(`/blocos/${blocoId}/dnssec`),
    onSuccess: () => void invalidar(),
  });
  const desativar = useMutation({
    mutationFn: () => api.delete(`/blocos/${blocoId}/dnssec`),
    onSuccess: () => void invalidar(),
  });
  const publicado = useMutation({
    mutationFn: (v: { zona: string; publicado: boolean }) =>
      api.put(`/blocos/${blocoId}/dnssec/publicado`, v),
    onSuccess: () => void invalidar(),
  });

  const iniciarRollover = useMutation({
    mutationFn: (zona: string) => api.post(`/blocos/${blocoId}/dnssec/rollover`, { zona }),
    onSuccess: () => void invalidar(),
  });
  const verificarRollover = useMutation({
    mutationFn: (zona: string) =>
      api.post<{ detectadoNovo?: boolean }>(`/blocos/${blocoId}/dnssec/rollover/verificar`, { zona }),
    onSuccess: () => void invalidar(),
  });
  const cancelarRollover = useMutation({
    mutationFn: (zona: string) =>
      api.delete(`/blocos/${blocoId}/dnssec/rollover?zona=${encodeURIComponent(zona)}`),
    onSuccess: () => void invalidar(),
  });

  if (!data || data.length === 0) return null;

  const assinadas = data.filter((z) => z.assinada).length;
  const total = data.length;
  const tudoAssinado = assinadas === total;

  return (
    <div className="rounded-card border border-borda bg-superficie">
      <button
        onClick={() => setAberto((a) => !a)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <span className="flex items-center gap-2">
          <ShieldCheck size={16} className={assinadas > 0 ? 'text-sucesso' : 'text-texto-mudo'} />
          <span className="text-[13px] font-bold">DNSSEC do reverso</span>
          <Pill tom={tudoAssinado ? 'ok' : assinadas > 0 ? 'aviso' : 'neutro'}>
            {assinadas === 0
              ? 'desligado'
              : tudoAssinado
                ? `${total} zonas assinadas`
                : `${assinadas}/${total} zonas`}
          </Pill>
        </span>
        <span className="text-[12px] text-texto-suave">{aberto ? 'ocultar' : 'ver'}</span>
      </button>

      {aberto && (
        <div className="space-y-3 border-t border-borda-suave px-4 py-3">
          {assinadas === 0 ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-[12.5px] leading-relaxed text-texto-suave">
                Assina as {total} zonas reversas deste bloco. Depois, publique cada DS na
                delegação reversa correspondente no registro.br.
              </p>
              <Botao
                variante="primario"
                tamanho="tabela"
                onClick={() => ativar.mutate()}
                carregando={ativar.isPending}
              >
                Ativar DNSSEC
              </Botao>
            </div>
          ) : (
            <>
              <div className="flex justify-end">
                <button
                  onClick={() => desativar.mutate()}
                  disabled={desativar.isPending}
                  className="text-[12px] font-semibold text-texto-suave hover:text-erro"
                >
                  desativar tudo
                </button>
              </div>
              {data.map((z) => (
                <div key={z.zona} className="rounded-chip border border-borda bg-fundo p-3">
                  <p className="font-mono text-[12.5px] font-semibold">{z.zona}</p>
                  <div className="mt-1.5">
                    <ListaDs ds={z.ds} />
                  </div>
                  <label className="mt-2 flex items-center gap-2 text-[12px]">
                    <input
                      type="checkbox"
                      checked={z.dsPublicado}
                      onChange={(e) => publicado.mutate({ zona: z.zona, publicado: e.target.checked })}
                      className="size-4 accent-marca"
                    />
                    <span className={z.dsPublicado ? 'font-semibold text-sucesso' : 'text-texto-medio'}>
                      DS publicado na delegação reversa
                    </span>
                  </label>

                  <SecaoRollover
                    assinada={z.assinada}
                    rollover={z.rollover}
                    aoIniciar={() => iniciarRollover.mutate(z.zona)}
                    iniciando={iniciarRollover.isPending && iniciarRollover.variables === z.zona}
                    aoVerificar={() => verificarRollover.mutate(z.zona)}
                    verificando={verificarRollover.isPending && verificarRollover.variables === z.zona}
                    aoCancelar={() => cancelarRollover.mutate(z.zona)}
                    cancelando={cancelarRollover.isPending && cancelarRollover.variables === z.zona}
                    detectadoNovo={
                      verificarRollover.variables === z.zona
                        ? verificarRollover.data?.detectadoNovo
                        : undefined
                    }
                  />
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
